/* One product selection and one guarded purchase path, shared with the hero. */
export class MessengerCommerce {
  constructor(root) {
    this.root = root;
    this.document = root.ownerDocument;
    this.window = this.document.defaultView;
    this.viewer = root.querySelector('bv-messenger-explorer');
    this.button = root.querySelector('[data-bvmx-commerce-add]');
    this.price = root.querySelector('[data-bvmx-commerce-price]');
    this.action = root.querySelector('[data-bvmx-commerce-action]');
    this.selection = root.querySelector('[data-bvmx-commerce-selection]');
    this.notice = root.querySelector('[data-bvmx-commerce-notice]');
    this.config = JSON.parse(root.querySelector('[data-bvmx-commerce-product]').textContent);
    this.variants = this.config.variants || [];
    this.listeners = [];
    this.observers = [];
    this.destroyed = false;
    this.queued = false;
    this.applyingProduct = false;
    this.add = () => this.purchase();
    this.onProduct = event => {
      if (event.type === 'bellvari:variant-change' || event.target?.closest?.('[data-bv-option-value],[data-bv-colorway],[data-bv-qty],[data-bv-qty-plus],[data-bv-qty-minus],[data-bv-variant-id]')) this.schedule();
    };
    this.onPalette = event => this.choosePalette(event);
    this.listen(this.button, 'click', this.add);
    this.listen(this.viewer, 'bvmx:palette-change', this.onPalette);
    for (const name of ['click', 'input', 'change', 'bellvari:variant-change']) this.listen(this.document, name, this.onProduct);
    this.observe(this.viewer, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-busy'] });
    this.observeHero();
    this.observeVisibility();
    this.sync();
    this.stabilizeInitialAnchor();
    this.window.customElements?.whenDefined('bv-messenger-explorer').then(() => this.schedule());
  }

  listen(target, type, listener) {
    target?.addEventListener(type, listener);
    this.listeners.push([target, type, listener]);
  }

  observe(target, options) {
    if (!target || !this.window.MutationObserver) return;
    const observer = new this.window.MutationObserver(records => {
      // The purchase panel lives inside the viewer. Its own label/busy updates
      // must not schedule another sync through the viewer subtree observer.
      const externalChange = records.some(record => {
        const node = record.target;
        return !(node?.closest?.('[data-bv-explorer-commerce]') || node?.parentElement?.closest?.('[data-bv-explorer-commerce]'));
      });
      if (externalChange) this.schedule();
    });
    observer.observe(target, options);
    this.observers.push(observer);
  }

  observeHero() {
    this.observers.splice(1).forEach(observer => observer.disconnect());
    const hero = this.document.querySelector('[data-bv-form]');
    this.observe(hero, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-busy'] });
  }

  observeVisibility() {
    if (!this.window.IntersectionObserver || !this.document.body) return;
    try {
      this.visibilityObserver = new this.window.IntersectionObserver(entries => {
        if (this.destroyed) return;
        const entry = entries.find(item => item.target === this.root);
        if (entry) this.document.body.classList.toggle('bv-messenger-in-view', entry.isIntersecting);
      }, { threshold: 0 });
      this.visibilityObserver.observe(this.root);
    } catch (_) {
      this.visibilityObserver?.disconnect();
      this.document.body.classList.remove('bv-messenger-in-view');
    }
  }

  stabilizeInitialAnchor() {
    if (this.window.location?.hash !== '#messenger-explorer' || !this.root.scrollIntoView) return;
    this.anchorActive = true;
    this.anchorListeners = [];
    const listen = (target, name, callback, options) => {
      target.addEventListener(name, callback, options);
      this.anchorListeners.push([target, name, callback, options]);
    };
    const stop = () => this.stopInitialAnchor();
    // A deliberate gesture owns the scroll from this point onward, even while
    // fonts, reviews or the hero's photographs are still finding their size.
    for (const name of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
      listen(this.window, name, stop, { capture: true, passive: true });
    }
    const align = () => {
      if (!this.anchorActive || this.destroyed || this.anchorFrame != null) return;
      const run = () => {
        this.anchorFrame = null;
        if (this.anchorActive && !this.destroyed && this.root.isConnected !== false) {
          this.root.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      };
      this.anchorFrameUsesTimeout = !this.window.requestAnimationFrame;
      this.anchorFrame = this.anchorFrameUsesTimeout ? this.window.setTimeout(run, 0) : this.window.requestAnimationFrame(run);
    };
    if (this.window.ResizeObserver) {
      this.anchorResizeObserver = new this.window.ResizeObserver(align);
      this.anchorResizeObserver.observe(this.root);
      const hero = this.document.querySelector('.bv-hero') || this.document.querySelector('[data-bv-form]');
      if (hero) this.anchorResizeObserver.observe(hero);
    }
    listen(this.window, 'load', align, { once: true });
    this.document.fonts?.ready?.then(align, () => {});
    this.anchorTimer = this.window.setTimeout(stop, 6000);
    align();
  }

  stopInitialAnchor() {
    this.anchorActive = false;
    this.anchorResizeObserver?.disconnect();
    this.anchorListeners?.forEach(([target, name, callback, options]) => target.removeEventListener(name, callback, options));
    this.anchorListeners = [];
    if (this.anchorTimer != null) this.window.clearTimeout(this.anchorTimer);
    if (this.anchorFrame != null) {
      if (this.anchorFrameUsesTimeout) this.window.clearTimeout(this.anchorFrame);
      else this.window.cancelAnimationFrame?.(this.anchorFrame);
    }
    this.anchorFrame = null;
  }

  schedule() {
    if (this.queued || this.destroyed) return;
    this.queued = true;
    queueMicrotask(() => {
      this.queued = false;
      if (!this.destroyed) this.sync();
    });
  }

  currentVariant() {
    const id = this.document.querySelector('[data-bv-variant-id]')?.value;
    return this.variants.find(variant => String(variant.id) === String(id));
  }

  paletteFor(variant) {
    const colour = variant?.option1 || '';
    return /black/i.test(colour) ? 'black' : /brown|crazy horse/i.test(colour) ? 'crazy-horse' : null;
  }

  sync() {
    if (this.destroyed) return;
    const variant = this.currentVariant();
    const hero = this.document.querySelector('[data-bv-form] [data-bv-atc]');
    const busy = hero?.getAttribute('aria-busy') === 'true';
    const available = Boolean(variant?.available && hero && !hero.disabled);
    this.button.disabled = !available || busy;
    this.button.setAttribute('aria-busy', String(busy));
    this.action.textContent = busy ? 'Adding…' : variant?.available ? 'Add to Bag' : 'Sold out';
    if (!variant) {
      this.notice.hidden = false;
      this.notice.textContent = 'Choose your colour in the product options above.';
      return;
    }
    this.notice.hidden = true;
    this.selection.textContent = variant.option1;
    const quantity = Number(this.document.querySelector('[data-bv-qty]')?.value || 1);
    const total = this.document.querySelector('[data-bv-total]')?.textContent.trim();
    try {
      // Both the JSON price and currency are rendered by the active Shopify market.
      this.price.textContent = total || new Intl.NumberFormat(this.config.locale || 'en', {
        style: 'currency', currency: this.config.currency || 'CAD'
      }).format(variant.price * (Number.isSafeInteger(quantity) && quantity > 0 ? quantity : 1) / 100);
    } catch (_) { if (total) this.price.textContent = total; }
    this.button.setAttribute('aria-label', `Add The Messenger in ${variant.option1} to bag, ${this.price.textContent}`);
    this.desiredPalette = this.paletteFor(variant);
    const missingModel = this.desiredPalette && this.viewer?.initialized &&
      typeof this.viewer.paletteAvailable === 'function' && !this.viewer.paletteAvailable(this.desiredPalette);
    if (this.viewer) {
      const embeddedCommerce = this.viewer.querySelector('[data-bv-explorer-commerce]');
      this.viewer.hidden = Boolean(missingModel && !embeddedCommerce);
      this.viewer.toggleAttribute('data-model-unavailable', Boolean(missingModel && embeddedCommerce));
    }
    if (missingModel) {
      this.notice.hidden = false;
      this.notice.textContent = 'The 3D view is unavailable for this colour. See the product photos above.';
      return;
    }
    if (!this.desiredPalette || !this.viewer?.initialized || this.viewer.loading || typeof this.viewer.setPalette !== 'function' || this.viewer.palette === this.desiredPalette) return;
    // setPalette emits synchronously before awaiting the GLB. Suppress that echo;
    // a busy viewer is retried by its aria-busy/child-list observer after loading.
    this.applyingProduct = true;
    let change;
    try { change = this.viewer.setPalette(this.desiredPalette); }
    finally { this.applyingProduct = false; }
    Promise.resolve(change).catch(() => {}).then(() => {
      if (!this.destroyed && this.currentVariant() && this.paletteFor(this.currentVariant()) !== this.desiredPalette) this.schedule();
    });
  }

  choosePalette(event) {
    if (this.applyingProduct || this.destroyed) return;
    const palette = event.detail?.palette;
    if (!['black', 'crazy-horse'].includes(palette)) return;
    const current = this.currentVariant();
    if (this.paletteFor(current) === palette) return;
    const variant = this.variants.find(item => this.paletteFor(item) === palette &&
      (!current || (item.option2 === current.option2 && item.option3 === current.option3)));
    const group = this.document.querySelector('[data-bv-variant-group]');
    const option = Array.from(group?.querySelectorAll('[data-bv-option-value]') || [])
      .find(button => button.dataset.bvOptionValue === variant?.option1);
    if (option && !option.disabled) option.click();
    // The hero click updates its hidden ID, image, totals and engraving bridge.
    // If a matching option was unavailable, restore the actual product choice.
    this.schedule();
  }

  purchase() {
    this.sync();
    if (this.button.disabled) return;
    const hero = this.document.querySelector('[data-bv-form] [data-bv-atc]');
    if (!hero || hero.disabled) return;
    // Its existing capture handler retains quantity, engraving validation,
    // gift inclusion, pending-add recovery and the shared cart drawer.
    hero.click();
    this.schedule();
  }

  destroy() {
    this.destroyed = true;
    this.stopInitialAnchor();
    this.listeners.forEach(([target, type, listener]) => target?.removeEventListener(type, listener));
    this.observers.forEach(observer => observer.disconnect());
    this.visibilityObserver?.disconnect();
    this.document.body?.classList.remove('bv-messenger-in-view');
  }
}

if (typeof document !== 'undefined') {
  const mounted = new Map();
  const mount = () => {
    for (const [root, controller] of mounted) {
      if (!root.isConnected) { controller.destroy(); mounted.delete(root); }
    }
    document.querySelectorAll('[data-bvmx-commerce]').forEach(root => {
      if (!root.querySelector('[data-bvmx-commerce-product]') || mounted.has(root)) return;
      try { mounted.set(root, new MessengerCommerce(root)); } catch (_) { /* Product form remains available if a theme editor draft is incomplete. */ }
    });
    mounted.forEach(controller => { controller.observeHero(); controller.schedule(); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, controller] of mounted) {
      if (event.target.contains(root)) { controller.destroy(); mounted.delete(root); }
    }
  });
}
